import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { equipmentSafetyKey, getEquipmentSafety, openChecklistItems } from '../../services/equipmentSafetyService';
import './EquipmentSafety.css';

/**
 * Dashboard prompt (Reef Guard #1828): while safety checklist items are open
 * (not answered, or answered "no"), point to the checklist. Renders nothing
 * while loading, on error or once everything is answered.
 */
export function EquipmentSafetyNotice({ aquariumId }: { aquariumId: string }) {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: equipmentSafetyKey(aquariumId),
    queryFn: () => getEquipmentSafety(aquariumId),
    enabled: Boolean(aquariumId),
    retry: false,
  });
  if (!query.data) return null;
  const open = openChecklistItems(query.data);
  if (open.length === 0) return null;
  const unanswered = open.filter((c) => c.answer === null).length;
  const no = open.length - unanswered;
  return (
    <Alert
      tone="info"
      live="off"
      titleLevel={2}
      title={t('equipmentSafety.notice.title')}
      data-testid="equipment-safety-notice"
      action={
        <Link className="equipment-safety__link" to={`/aquariums/${aquariumId}/equipment-safety`}>
          {t('equipmentSafety.notice.action')}
        </Link>
      }
    >
      {no > 0
        ? t('equipmentSafety.notice.no', { count: no })
        : t('equipmentSafety.notice.unanswered', { count: unanswered })}
    </Alert>
  );
}
